import React, { useEffect, useState } from "react";
import { Drawer, Box, Grid, TextField, Typography, IconButton, Button as MuiButton, CircularProgress, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationMedicoSchema } from "../../../schemas/validationSchema.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import medicoService from "../../../services/medicoService.ts";
import enderecoService from "../../../services/enderecoService.ts";
import { Medico } from "../../../models/Medico.ts";
import { Endereco } from "../../../models/Endereco.ts";
import { formatBrazilPhone } from "../../../utils/phone.util.ts";
import { maskCep } from "../../../utils/address.util.ts";

interface UpdateMedicoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (r: any) => void;
  medico: any;
}

// Este app não tem ThemeProvider do MUI — sem ele, estados como label,
// borda e principalmente "disabled" (campo CRM, sempre travado) usam os
// tokens de tema claro padrão do MUI, com contraste baixo demais pra
// aparecer num fundo escuro (foi exatamente o bug do campo CRM "sumindo"
// no modal anterior). Cores explícitas via CSS vars resolvem de vez.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiInputLabel-root.Mui-disabled": { color: "var(--ink-faint)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
    "&.Mui-disabled fieldset": { borderColor: "var(--border)" },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "var(--ink-faint)",
    color: "var(--ink-faint)",
  },
  "& .MuiFormHelperText-root": { color: "var(--red)" },
};

const scrollSx = {
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--border-strong)", borderRadius: 4 },
};

const UpdateMedicoModal: React.FC<UpdateMedicoModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  medico,
}) => {
  const { showFeedback } = useFeedback();
  const [isLoading, setIsLoading] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationMedicoSchema),
  });

  useEffect(() => {
    if (medico) {
      reset({
        nome: medico.nome,
        email: medico.email,
        telefone: formatBrazilPhone(medico.telefone),
        crm: medico.crm,
        endereco: {
          cep: medico.endereco?.cep ? maskCep(medico.endereco.cep) : "",
          bairro: medico.endereco?.bairro || "",
          cidade: medico.endereco?.cidade || "",
          uf: medico.endereco?.uf || "",
          rua: medico.endereco?.rua || "",
          numero: medico.endereco?.numero || "",
        },
      });
    }
  }, [medico, reset]);

  const handleCepChange = async (value: string) => {
    const masked = maskCep(value);
    setValue("endereco.cep", masked, { shouldValidate: true });
    const raw = masked.replace(/\D/g, "");
    if (raw.length === 8) {
      setBuscandoCep(true);
      await enderecoService.getAddressByZipCode(raw, "endereco", setValue, showFeedback);
      setBuscandoCep(false);
    }
  };

  const onSubmit = async (data: any) => {
    const endereco = new Endereco(
      data.endereco.rua,
      data.endereco.bairro,
      data.endereco.cep,
      data.endereco.numero,
      data.endereco.cidade,
      data.endereco.uf
    );
    const updated = new Medico(medico.id, data.nome, data.email, data.telefone, data.crm, endereco);

    try {
      setIsLoading(true);
      const resp = await medicoService.update(updated);
      showFeedback(resp.message, "success");
      onUpdate?.(resp);
      onClose();
    } catch (err: any) {
      showFeedback(err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          bgcolor: "var(--surface)",
          color: "var(--ink)",
          borderLeft: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Cabeçalho fixo */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid var(--border)", flex: "0 0 auto" }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Alterar Médico</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "var(--ink-faint)" }} aria-label="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Corpo com scroll — flex:1 + overflowY:auto, o resto do Drawer fica fixo */}
      <Box
        component="form"
        id="update-medico-form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, ...scrollSx }}
      >
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField
              label="Nome*" size="small" fullWidth sx={fieldSx}
              {...register("nome")} autoComplete="off" error={!!errors.nome} helperText={errors.nome?.message as string}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="CRM*" size="small" fullWidth disabled sx={fieldSx}
              {...register("crm")} error={!!errors.crm} helperText={errors.crm?.message as string}
            />
          </Grid>

          <Grid item xs={7}>
            <TextField
              label="E-mail" size="small" fullWidth sx={fieldSx}
              {...register("email")} error={!!errors.email} helperText={errors.email?.message as string}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              label="Telefone*" size="small" fullWidth sx={fieldSx}
              value={watch("telefone") || ""}
              onChange={(e) => setValue("telefone", formatBrazilPhone(e.target.value), { shouldValidate: true })}
              error={!!errors.telefone} helperText={errors.telefone?.message as string}
            />
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--ink)" }}>
              Endereço
            </Typography>
          </Grid>

          <Grid item xs={7}>
            <TextField
              label="CEP" size="small" fullWidth sx={fieldSx}
              value={watch("endereco.cep") || ""}
              onChange={(e) => handleCepChange(e.target.value)}
              disabled={buscandoCep}
              error={!!(errors.endereco as any)?.cep}
              helperText={(errors.endereco as any)?.cep?.message}
              InputProps={{ endAdornment: buscandoCep ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : undefined }}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField label="Bairro" size="small" fullWidth sx={fieldSx} {...register("endereco.bairro")} />
          </Grid>

          <Grid item xs={8}>
            <TextField label="Cidade" size="small" fullWidth sx={fieldSx} {...register("endereco.cidade")} />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="UF" size="small" fullWidth sx={fieldSx} {...register("endereco.uf")}
              inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
            />
          </Grid>

          <Grid item xs={8}>
            <TextField label="Rua" size="small" fullWidth sx={fieldSx} {...register("endereco.rua")} />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Número" size="small" fullWidth sx={fieldSx} {...register("endereco.numero")} />
          </Grid>
        </Grid>
      </Box>

      {/* Rodapé fixo */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, py: 2.5, borderTop: "1px solid var(--border)", flex: "0 0 auto" }}>
        <MuiButton onClick={onClose} disabled={isLoading} sx={{ color: "var(--ink-soft)" }}>
          Cancelar
        </MuiButton>
        <MuiButton type="submit" form="update-medico-form" variant="contained" disabled={isLoading} disableElevation>
          {isLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Salvar Alterações"}
        </MuiButton>
      </Box>
    </Drawer>
  );
};

export default UpdateMedicoModal;
